import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { Loader2, Boxes, ArrowUpRight, ArrowDownRight, Upload, RefreshCcw, Send, Coins, CheckCircle2, Anchor } from 'lucide-react';
import BranchShell from '../components/BranchShell';
import { useBranchAuth, API } from '../context/BranchAuthContext';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatDate } from '../lib/dateFormat';

/**
 * BranchPallets — branch-portal Pallet Ledger. Shows the branch's
 * running pallet balance, transaction history, and two return flows:
 *   Option A — Physical return (warehouse credits inventory on
 *              confirmation)
 *   Option B — Sell & Deposit (branch uploads bank slip; admin
 *              settles → branch liability reduced; warehouse
 *              inventory unchanged)
 */

const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const shortDate = (iso) => formatDate(iso) || '—';


const TxRow = ({ tx }) => {
  const isCredit = tx.qty >= 0;
  const absQty = Math.abs(tx.qty || 0);
  // For opening_balance rows, backend sends `unit_price` (per-pallet)
  // and `amount = qty * unit_price` (line total). For normal txs,
  // amount = qty * unit_price too, so `amount/qty` reconstructs the
  // unit price. `tx.unit_price` wins when present.
  const unitPrice = Number(tx.unit_price) > 0
    ? Number(tx.unit_price)
    : (absQty > 0 ? Math.abs(Number(tx.amount || 0)) / absQty : 0);
  const kindLabel = {
    grn_receipt: 'GRN receipt',
    order_dispatch: 'Order dispatch',
    physical_return: 'Physical return',
    deposit_settlement: 'Deposit settlement',
    opening_balance: 'Opening Balance',
  }[tx.kind] || tx.kind;
  if (tx.kind === 'opening_balance') {
    const typeLabel = tx.pallet_type ? tx.pallet_type.charAt(0).toUpperCase() + tx.pallet_type.slice(1) : '';
    return (
      <tr data-testid={`branch-pallet-tx-${tx.id}`} className="bg-sky-50/70">
        <td className="p-2 text-xs">{tx.ts ? shortDate(tx.ts) : '—'}</td>
        <td className="p-2 text-xs font-semibold text-sky-900 inline-flex items-center gap-1"><Anchor className="w-3 h-3" /> Opening Balance{typeLabel ? ` - ${typeLabel}` : ''}</td>
        <td className="p-2 text-xs capitalize text-neutral-600">{tx.pallet_type || '—'}</td>
        <td className="p-2 text-right font-semibold text-sky-900">{fmtInt(tx.qty)}</td>
        <td className="p-2 text-right text-xs text-sky-900" data-testid={`branch-pallet-tx-unit-price-${tx.id}`}>OMR {fmt(unitPrice)}</td>
        <td className="p-2 text-right text-xs text-sky-900" data-testid={`branch-pallet-tx-total-${tx.id}`}>OMR {fmt(tx.amount)}</td>
        <td className="p-2 text-xs text-neutral-500">—</td>
        <td className="p-2 text-xs text-neutral-500 truncate max-w-[180px]">{tx.notes || 'Carried forward'}</td>
      </tr>
    );
  }
  return (
    <tr data-testid={`branch-pallet-tx-${tx.id}`}>
      <td className="p-2 text-xs">{shortDate(tx.ts)}</td>
      <td className="p-2 text-xs">{kindLabel}</td>
      <td className="p-2 text-xs capitalize">{tx.pallet_type}</td>
      <td className={`p-2 text-right font-semibold ${isCredit ? 'text-emerald-700' : 'text-red-600'}`}>
        {isCredit ? <ArrowUpRight className="w-3.5 h-3.5 inline-block" /> : <ArrowDownRight className="w-3.5 h-3.5 inline-block" />}
        {fmtInt(absQty)}
      </td>
      <td className="p-2 text-right text-xs" data-testid={`branch-pallet-tx-unit-price-${tx.id}`}>OMR {fmt(unitPrice)}</td>
      <td className="p-2 text-right text-xs" data-testid={`branch-pallet-tx-total-${tx.id}`}>OMR {fmt(tx.amount)}</td>
      <td className="p-2 text-xs text-neutral-600 truncate">{tx.order_number || '—'}</td>
      <td className="p-2 text-xs text-neutral-500 truncate max-w-[180px]">{tx.notes || '—'}</td>
    </tr>
  );
};


const BranchPallets = () => {
  const { token, authHeaders } = useBranchAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(''); // 'physical' | 'deposit'
  const [form, setForm] = useState({
    quantity: '',
    pallet_type: 'plastic',
    order_reference: '',
    amount: '',
    notes: '',
  });
  const [slip, setSlip] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/branch/pallets/history`, { headers: authHeaders });
      setData(res);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || err?.message });
    } finally { setLoading(false); }
  }, [authHeaders, toast]);

  useEffect(() => { if (token) refresh(); }, [token, refresh]);

  const resetForm = () => {
    setForm({ quantity: '', pallet_type: 'plastic', order_reference: '', amount: '', notes: '' });
    setSlip(null);
    setMode('');
  };

  const submitPhysical = async () => {
    const q = parseInt(form.quantity, 10);
    if (!q || q <= 0) { toast({ title: 'Enter a positive quantity' }); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/branch/pallets/returns`, {
        quantity: q,
        pallet_type: form.pallet_type,
        order_reference: form.order_reference.trim(),
        notes: form.notes.trim(),
      }, { headers: authHeaders });
      toast({ title: 'Return submitted', description: 'Warehouse will confirm receipt.' });
      resetForm();
      await refresh();
    } catch (err) {
      toast({ title: 'Submit failed', description: err?.response?.data?.detail || err?.message });
    } finally { setSubmitting(false); }
  };

  const submitDeposit = async () => {
    const q = parseInt(form.quantity, 10);
    const amt = parseFloat(form.amount);
    if (!q || q <= 0) { toast({ title: 'Enter pallet quantity' }); return; }
    if (!amt || amt <= 0) { toast({ title: 'Enter deposit amount' }); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('quantity', String(q));
      fd.append('amount', String(amt));
      fd.append('pallet_type', form.pallet_type);
      fd.append('order_reference', form.order_reference.trim());
      fd.append('notes', form.notes.trim());
      if (slip) fd.append('slip', slip);
      await axios.post(`${API}/branch/pallets/deposits`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Deposit submitted', description: 'Admin will confirm the settlement.' });
      resetForm();
      await refresh();
    } catch (err) {
      toast({ title: 'Submit failed', description: err?.response?.data?.detail || err?.message });
    } finally { setSubmitting(false); }
  };

  if (!token) return <Navigate to="/branch/login" replace />;

  const balance = data?.pallet_balance ?? 0;
  const isDebtor = balance > 0;

  return (
    <BranchShell>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0F172A]" data-testid="branch-pallets-title">Pallet Ledger</h1>
          <div className="text-xs text-neutral-500 mt-1">Track pallets received, physical returns, and deposit settlements.</div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="text-xs font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-3 py-1.5 rounded-full inline-flex items-center gap-1"
          data-testid="branch-pallets-refresh"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-neutral-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : (
        <>
          {/* Balance + Liability tiles — two equally prominent cards
              side-by-side. Balance is the physical pallet count owed;
              Liability is the total monetary amount owed across every
              pallet transaction (including opening balances). */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className={`rounded-2xl border p-5 ${isDebtor ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="branch-pallets-balance">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-bold text-neutral-600">
                <Boxes className="w-4 h-4" /> Current pallet balance
              </div>
              <div className={`text-4xl font-bold mt-1 ${isDebtor ? 'text-amber-900' : 'text-emerald-900'}`} data-testid="branch-pallets-balance-qty">
                {fmtInt(balance)}
              </div>
              <div className={`text-xs mt-1 ${isDebtor ? 'text-amber-800' : 'text-emerald-800'}`}>
                {isDebtor
                  ? `You currently hold ${fmtInt(balance)} pallet${balance === 1 ? '' : 's'} of ours. Return them or settle via deposit below.`
                  : 'You are square with the warehouse.'}
              </div>
            </div>
            <div className={`rounded-2xl border p-5 ${(data?.monetary_liability || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="branch-pallets-liability">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-bold text-neutral-600">
                <Coins className="w-4 h-4" /> Total liability
              </div>
              <div className={`text-4xl font-bold mt-1 tabular-nums ${(data?.monetary_liability || 0) > 0 ? 'text-red-900' : 'text-emerald-900'}`} data-testid="branch-pallets-liability-amount">
                OMR {fmt(data?.monetary_liability || 0)}
              </div>
              <div className={`text-xs mt-1 ${(data?.monetary_liability || 0) > 0 ? 'text-red-800' : 'text-emerald-800'}`}>
                Sum of all pallet transactions plus opening balances.
              </div>
            </div>
          </div>

          {/* Opening balance callout — only shown when admin has set one.
              Two lines (plastic + wooden) when either is non-zero. */}
          {((data?.opening_plastic?.opening_qty > 0 || data?.opening_plastic?.opening_amount > 0)
            || (data?.opening_wooden?.opening_qty > 0 || data?.opening_wooden?.opening_amount > 0)) && (
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 mb-4" data-testid="branch-pallets-opening">
              <div className="flex items-center gap-2 mb-2">
                <Anchor className="w-4 h-4 text-sky-700" />
                <span className="text-xs font-bold uppercase tracking-wide text-sky-900">Opening balance</span>
                <span className="text-[10px] text-sky-700/70 ml-auto">Carried forward — included in running balance</span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="text-sky-900 font-semibold">Plastic</div>
                <div className="text-sky-900">Qty: <b data-testid="branch-pallets-opening-plastic-qty">{fmtInt(data.opening_plastic?.opening_qty || 0)}</b></div>
                <div className="text-sky-900">Unit Price: <b data-testid="branch-pallets-opening-plastic-unit-price">OMR {fmt(data.opening_plastic?.opening_unit_price || 0)}</b></div>
                <div className="text-sky-900">Total: <b data-testid="branch-pallets-opening-plastic-amount">OMR {fmt(data.opening_plastic?.opening_amount || 0)}</b></div>
                <div className="text-sky-900 font-semibold">Wooden</div>
                <div className="text-sky-900">Qty: <b data-testid="branch-pallets-opening-wooden-qty">{fmtInt(data.opening_wooden?.opening_qty || 0)}</b></div>
                <div className="text-sky-900">Unit Price: <b data-testid="branch-pallets-opening-wooden-unit-price">OMR {fmt(data.opening_wooden?.opening_unit_price || 0)}</b></div>
                <div className="text-sky-900">Total: <b data-testid="branch-pallets-opening-wooden-amount">OMR {fmt(data.opening_wooden?.opening_amount || 0)}</b></div>
                <div className="text-sky-900 font-bold">Total</div>
                <div className="text-sky-900 font-bold">Qty: <b data-testid="branch-pallets-opening-qty">{fmtInt(data.opening_qty || 0)}</b></div>
                <div />
                <div className="text-sky-900 font-bold">Total: <b data-testid="branch-pallets-opening-amount">OMR {fmt(data.opening_amount || 0)}</b></div>
              </div>
            </div>
          )}

          {/* Return actions */}
          {!mode && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={() => setMode('physical')}
                className="rounded-2xl bg-white border border-neutral-200 hover:border-indigo-300 hover:bg-indigo-50 p-4 text-left transition"
                data-testid="branch-pallets-mode-physical"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Send className="w-5 h-5 text-indigo-600" />
                  <span className="font-bold text-neutral-900">Physical return</span>
                </div>
                <div className="text-xs text-neutral-500">Send pallets back to warehouse. Warehouse confirms on receipt.</div>
              </button>
              <button
                type="button"
                onClick={() => setMode('deposit')}
                className="rounded-2xl bg-white border border-neutral-200 hover:border-emerald-300 hover:bg-emerald-50 p-4 text-left transition"
                data-testid="branch-pallets-mode-deposit"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Coins className="w-5 h-5 text-emerald-600" />
                  <span className="font-bold text-neutral-900">Sell &amp; deposit</span>
                </div>
                <div className="text-xs text-neutral-500">Sell pallets, deposit the amount to our bank, upload the slip. Admin confirms.</div>
              </button>
            </div>
          )}

          {mode === 'physical' && (
            <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 mb-4 space-y-3" data-testid="branch-pallets-physical-form">
              <div className="text-sm font-bold text-indigo-900">Initiate physical pallet return</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-neutral-600 uppercase">Quantity</label>
                  <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 5" data-testid="branch-pallets-physical-qty" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-neutral-600 uppercase">Type</label>
                  <select value={form.pallet_type} onChange={(e) => setForm({ ...form, pallet_type: e.target.value })} className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm" data-testid="branch-pallets-physical-type">
                    <option value="plastic">Plastic</option>
                    <option value="wooden">Wooden</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-neutral-600 uppercase">Order ref (optional)</label>
                  <Input value={form.order_reference} onChange={(e) => setForm({ ...form, order_reference: e.target.value })} placeholder="e.g. MR-1234" data-testid="branch-pallets-physical-ref" />
                </div>
              </div>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" data-testid="branch-pallets-physical-notes" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={resetForm} className="rounded-full h-9">Cancel</Button>
                <Button onClick={submitPhysical} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-9" data-testid="branch-pallets-physical-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                  Submit return
                </Button>
              </div>
            </div>
          )}

          {mode === 'deposit' && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 mb-4 space-y-3" data-testid="branch-pallets-deposit-form">
              <div className="text-sm font-bold text-emerald-900">Sell &amp; deposit settlement</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-neutral-600 uppercase">Pallet qty sold</label>
                  <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 5" data-testid="branch-pallets-deposit-qty" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-neutral-600 uppercase">Amount deposited (OMR)</label>
                  <Input type="number" min="0" step="0.001" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.000" data-testid="branch-pallets-deposit-amount" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-neutral-600 uppercase">Type</label>
                  <select value={form.pallet_type} onChange={(e) => setForm({ ...form, pallet_type: e.target.value })} className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm" data-testid="branch-pallets-deposit-type">
                    <option value="plastic">Plastic</option>
                    <option value="wooden">Wooden</option>
                  </select>
                </div>
              </div>
              <Input value={form.order_reference} onChange={(e) => setForm({ ...form, order_reference: e.target.value })} placeholder="Order/reference (optional)" data-testid="branch-pallets-deposit-ref" />
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" data-testid="branch-pallets-deposit-notes" />
              <div>
                <label className="text-[10px] font-bold text-neutral-600 uppercase mb-1 block">Bank deposit slip (uploads to Google Drive)</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                  onChange={(e) => setSlip(e.target.files?.[0] || null)}
                  className="text-xs"
                  data-testid="branch-pallets-deposit-slip"
                />
                {slip && <div className="text-xs text-emerald-800 mt-1">{slip.name} ({Math.round(slip.size / 1024)} KB)</div>}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={resetForm} className="rounded-full h-9">Cancel</Button>
                <Button onClick={submitDeposit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-9" data-testid="branch-pallets-deposit-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  Submit deposit
                </Button>
              </div>
            </div>
          )}

          {/* Pending returns */}
          {(data?.pending_returns || []).length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 mb-4" data-testid="branch-pallets-pending">
              <div className="text-xs uppercase font-bold text-amber-900 mb-2">Pending confirmation</div>
              <div className="space-y-1.5">
                {data.pending_returns.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 justify-between text-xs bg-white rounded-lg border border-amber-200 p-2" data-testid={`branch-pallets-pending-${r.id}`}>
                    <span className="capitalize font-semibold">{r.kind.replaceAll('_', ' ')}</span>
                    <span>{fmtInt(r.quantity)} pallets</span>
                    <span className="capitalize">{r.pallet_type}</span>
                    <span className="text-neutral-500">{shortDate(r.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transaction history */}
          <div className="rounded-2xl border border-neutral-200 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
                  <th className="p-2">Date</th>
                  <th className="p-2">Kind</th>
                  <th className="p-2">Type</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2 text-right">Unit Price</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2">Order</th>
                  <th className="p-2">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {(data?.transactions || []).length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center italic text-neutral-500" data-testid="branch-pallets-tx-empty">No pallet transactions yet.</td></tr>
                )}
                {(data?.transactions || []).map((tx) => <TxRow key={tx.id} tx={tx} />)}
              </tbody>
            </table>
          </div>
        </>
      )}
    </BranchShell>
  );
};

export default BranchPallets;
